import { useEffect, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { useAuth } from '../AuthContext';
import { authedFetch } from '../utils/authedFetch';
import {
  Box,
  Paper,
  Typography,
  Chip,
  CircularProgress,
  Table,
  TableHead,
  TableBody,
  TableRow,
  TableCell,
  Stack,
  TextField,
  Button
} from '@mui/material';
import PrintIcon from '@mui/icons-material/Print';

const ACCENT = 'rgb(219, 234, 254)';
const ACCENT_BORDER = '#60A5FA';

export default function SessionSummary() {
  const location = useLocation();
  const navigate = useNavigate();
  const { user, session, authLoading } = useAuth();
  const accessToken = session?.access_token;
  const liveScenarioId = location.state?.livescenarioid;
  const API_BASE = process.env.REACT_APP_API_URL;
  const [actions, setActions] = useState([]);
  const [summary, setSummary] = useState([]);
  const [loading, setLoading] = useState(true);
  const [facilitatorComments, setFacilitatorComments] = useState({});
  const [overallFeedback, setOverallFeedback] = useState('');
  const scen = summary[0] || null;

  useEffect(() => {
  if (!liveScenarioId || !accessToken) return;

  const loadSummary = async () => {
    try {
      setLoading(true);

      const [scenRes, actionsRes] = await Promise.all([
        authedFetch({
          accessToken,
          apiBase: API_BASE,
          path: `/api/livescenario/${liveScenarioId}/scen-summary`,
          navigate,
        }),
        authedFetch({
          accessToken,
          apiBase: API_BASE,
          path: `/api/livescenario/${liveScenarioId}/actions-summary`,
          navigate,
        }),
      ]);

      if (!scenRes.ok) throw new Error(`Scenario summary HTTP ${scenRes.status}`);
      if (!actionsRes.ok) throw new Error(`Action summary HTTP ${actionsRes.status}`);

      const scenData = await scenRes.json();
      const actionsData = await actionsRes.json();

      setSummary(scenData.summaryinfo ?? []);
      setActions(actionsData.actions ?? []);
    } catch (err) {
      console.error('Failed to load summary:', err);
    } finally {
      setLoading(false);
    }
  };

  loadSummary();
}, [liveScenarioId, accessToken, API_BASE, navigate]);

  const parseComment = (raw) => {
    if (!raw) return null;

    if (Array.isArray(raw)) return raw;

    if (typeof raw === 'string') {
      try {
        const parsed = JSON.parse(raw);
        return Array.isArray(parsed) ? parsed : raw;
      } catch {
        return raw;
      }
    }

    return String(raw);
  };

  const renderComment = (comment) => {
    const parsed = parseComment(comment);

    if (!parsed || parsed === '-') {
      return (
        <Typography variant="body2" color="text.secondary">
          —
        </Typography>
      );
    }

    if (Array.isArray(parsed)) {
      return (
        <Typography variant="body2" sx={{ fontSize: '12px' }}>
          {parsed.join(', ')}
        </Typography>
      );
    }

    return (
      <Typography variant="body2" sx={{ fontSize: '12px' }}>
        {parsed}
      </Typography>
    );
  };

  const handleFacilitatorCommentChange = (actionKey, value) => {
    setFacilitatorComments((prev) => ({
      ...prev,
      [actionKey]: value,
    }));
  };

  const handlePrint = () => {
    window.print();
  };

  function capitalise(str) {
    return str ? str.charAt(0).toUpperCase() + str.slice(1).toLowerCase() : '';
  };

return (
  <>
    <style>
      {`
        @media print {
          body {
            background: white !important;
          }

          .no-print {
            display: none !important;
          }

          .print-container {
            margin: 0 !important;
            padding: 0 !important;
            background: white !important;
          }

          .print-card {
            border: none !important;
            box-shadow: none !important;
            margin: 0 !important;
          }

          .summary-scroll-box {
            max-height: none !important;
            overflow: visible !important;
          }

          .MuiTableCell-root {
            font-size: 11px !important;
            padding: 6px !important;
          }

          textarea,
          input {
            border: none !important;
          }
        }
      `}
    </style>

<Box
  className="print-container"
  sx={{
    height: '100vh',
    width: '100%',
    bgcolor: '#F8FAFF',
    p: 2,
    overflowY: 'auto',
    boxSizing: 'border-box',
  }}
>
      <Paper
        className="print-card"
        elevation={0}
        sx={{
          border: `1px solid ${ACCENT_BORDER}`,
          borderRadius: 3,
          bgcolor: 'white',
          overflow: 'hidden',
          mr: { xs: 0, md: '35px' },
        }}
      >
        {/* Header */}
        <Box
          sx={{
            px: 2,
            py: 1.5,
            bgcolor: ACCENT,
            borderBottom: `1px solid ${ACCENT_BORDER}`,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            gap: 2,
            flexWrap: 'wrap',
          }}
        >
          <Box>
            <Typography sx={{ fontWeight: 900, fontSize: '1.1rem' }}>
              Session summary
            </Typography>

            <Typography variant="body2" color="text.secondary">
              Review participant actions and add facilitator feedback.
            </Typography>
          </Box>

          <Stack direction="row" spacing={1} alignItems="center" className="no-print">
            <Chip
              size="small"
              label={liveScenarioId ? `Live scenario #${liveScenarioId}` : '—'}
              sx={{ bgcolor: 'white' }}
            />

            <Button
              size="small"
              variant="contained"
              startIcon={<PrintIcon />}
              onClick={handlePrint}
              sx={{
                textTransform: 'none',
                fontWeight: 700,
                borderRadius: 2,
              }}
            >
              Print / save PDF
            </Button>
          </Stack>
        </Box>

        {/* Scenario summary cards */}
        <Box sx={{ p: 2 }}>
          {loading ? (
            <Box sx={{ display: 'flex', justifyContent: 'center', py: 6 }}>
              <CircularProgress />
            </Box>
          ) : (
            <>
              <Paper
                elevation={0}
                sx={{
                  p: 2,
                  border: '1px solid #E5E7EB',
                  borderRadius: 3,
                  bgcolor: '#F8FAFF',
                  mb: 2,
                }}
              >
                <Stack spacing={1.5}>
                  <Box>
                    <Typography sx={{ fontWeight: 900, fontSize: '1.25rem' }}>
                      {scen?.title || 'Untitled scenario'}
                    </Typography>

                    {scen?.description && (
                      <Typography color="text.secondary" sx={{ mt: 0.5 }}>
                        {scen.description}
                      </Typography>
                    )}
                  </Box>

                  <Stack
                    direction={{ xs: 'column', sm: 'row' }}
                    spacing={1}
                    useFlexGap
                    flexWrap="wrap"
                  >
                    <Chip
                      label={`Participant: ${capitalise(scen?.participant_name) || '—'}`}
                      variant="outlined"
                      sx={{ bgcolor: 'white' }}
                    />

                    <Chip
                      label={`Runtime: ${Math.round(Number(scen?.runtime || 0))} mins`}
                      variant="outlined"
                      sx={{ bgcolor: 'white' }}
                    />

                    <Chip
                      label={`${actions.length} recorded action${actions.length === 1 ? '' : 's'}`}
                      variant="outlined"
                      sx={{ bgcolor: 'white' }}
                    />
                  </Stack>
                </Stack>
              </Paper>

              {/* Overall feedback */}
              <Paper
                elevation={0}
                sx={{
                  p: 2,
                  border: '1px solid #E5E7EB',
                  borderRadius: 3,
                  mb: 2,
                }}
              >
                <Stack spacing={1}>
                  <Typography sx={{ fontWeight: 800 }}>
                    Overall facilitator feedback
                  </Typography>

                  <Typography variant="body2" color="text.secondary">
                    Add a global summary of performance, key learning points, and suggested next steps.
                  </Typography>

                  <TextField
                    multiline
                    minRows={4}
                    fullWidth
                    value={overallFeedback}
                    onChange={(e) => setOverallFeedback(e.target.value)}
                    placeholder="Overall feedback..."
                    variant="outlined"
                    sx={{
                      '& .MuiOutlinedInput-root': {
                        bgcolor: '#F8FAFF',
                        borderRadius: 2,
                      },
                    }}
                  />
                </Stack>
              </Paper>

              {/* Action table */}
              <Paper
                elevation={0}
                sx={{
                  border: '1px solid #E5E7EB',
                  borderRadius: 3,
                  overflow: 'hidden',
                }}
              >
                <Box
                  sx={{
                    px: 2,
                    py: 1.25,
                    bgcolor: '#F8FAFF',
                    borderBottom: '1px solid #E5E7EB',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'space-between',
                    gap: 1,
                    flexWrap: 'wrap',
                  }}
                >
                  <Typography sx={{ fontWeight: 800 }}>
                    Timeline
                  </Typography>

                  <Chip
                    size="small"
                    label={`${actions.length} entries`}
                    variant="outlined"
                    sx={{ bgcolor: 'white' }}
                  />
                </Box>

                <Box className="summary-scroll-box" sx={{ maxHeight: 620, overflowY: 'auto' }}>
                  <Table stickyHeader size="small" sx={{ bgcolor: 'white' }}>
                    <TableHead>
                      <TableRow>
                        <TableCell sx={{ fontWeight: 800, width: 110 }}>
                          Time
                        </TableCell>

                        <TableCell sx={{ fontWeight: 800, width: 180 }}>
                          Action
                        </TableCell>

                        <TableCell sx={{ fontWeight: 800 }}>
                          Session note
                        </TableCell>

                        <TableCell sx={{ fontWeight: 800, minWidth: 220 }}>
                          Facilitator comment
                        </TableCell>
                      </TableRow>
                    </TableHead>

                    <TableBody>
                      {actions.length === 0 ? (
                        <TableRow>
                          <TableCell
                            colSpan={4}
                            sx={{
                              textAlign: 'center',
                              color: 'text.secondary',
                              py: 4,
                            }}
                          >
                            No actions recorded.
                          </TableCell>
                        </TableRow>
                      ) : (
                        actions.map((a, idx) => {
                          const actionKey = a.id ?? `${a.action_type}-${idx}`;

                          return (
                            <TableRow
                              key={actionKey}
                              hover
                              sx={{
                                '&:nth-of-type(even)': {
                                  bgcolor: '#F8FAFF',
                                },
                              }}
                            >
                              <TableCell>
                                <Chip
                                  size="small"
                                  label={
                                    a.minutes_since_start != null
                                      ? `${a.minutes_since_start} min`
                                      : '—'
                                  }
                                  variant="outlined"
                                  sx={{ bgcolor: 'white' }}
                                />
                              </TableCell>

                              <TableCell>
                                <Typography
                                  sx={{
                                    fontWeight: 700,
                                    textTransform: 'capitalize',
                                  }}
                                >
                                  {a.action_description || '—'}
                                </Typography>
                              </TableCell>

                              <TableCell>
                                {renderComment(a.action_comment)}
                              </TableCell>

                              <TableCell>
                                <TextField
                                  size="small"
                                  multiline
                                  minRows={1}
                                  fullWidth
                                  value={facilitatorComments[actionKey] || ''}
                                  onChange={(e) =>
                                    handleFacilitatorCommentChange(actionKey, e.target.value)
                                  }
                                  placeholder="Add comment..."
                                  variant="outlined"
                                  sx={{
                                    '& .MuiOutlinedInput-root': {
                                      bgcolor: 'white',
                                      borderRadius: 2,
                                    },
                                  }}
                                />
                              </TableCell>
                            </TableRow>
                          );
                        })
                      )}
                    </TableBody>
                  </Table>
                </Box>
              </Paper>
            </>
          )}
        </Box>
      </Paper>
    </Box>
  </>
);
}